import React, { useState } from "react";
import HeroSection from "../components/gifting/HeroSection";
import GiftInfoForm from "../components/gifting/GiftInfoForm";
import AIResultsSection from "../components/gifting/AIResultsSection";
import CuratedGiftIdeas from "../components/gifting/CuratedGiftIdeas";
import RightSidebar from "../components/gifting/RightSidebar";
import { createSavedGift } from "../components/gifting/savedGiftSchema";
import "./AIGifting.css";

export default function AIGifting() {
  const [aiGifts, setAiGifts] = useState([]);
  const [savedGifts, setSavedGifts] = useState([]);
  const [recipientName, setRecipientName] = useState("");
  const [lastFormData, setLastFormData] = useState({});
  const [isLoadingAI, setIsLoadingAI] = useState(false);

  const buildStoreUrl = (source, query) => {
    const encoded = encodeURIComponent(query);

    if ((source || "").includes("Etsy")) {
      return `https://www.etsy.com/search?q=${encoded}`;
    }

    if ((source || "").includes("Target")) {
      return `https://www.target.com/s?searchTerm=${encoded}`;
    }

    if ((source || "").includes("Walmart")) {
      return `https://www.walmart.com/search?q=${encoded}`;
    }

    if ((source || "").includes("Best Buy")) {
      return `https://www.bestbuy.com/site/searchpage.jsp?st=${encoded}`;
    }

    return `https://www.amazon.com/s?k=${encoded}`;
  };

  const getBudgetText = (budget) => {
    if (Array.isArray(budget)) {
      return budget[0] ? `$${budget[0]}` : "Flexible";
    }

    return budget || "Flexible";
  };

  const createGiftIdeas = (formData) => {
    const name = formData.recipientName || "the recipient";
    const relationship = formData.relationship || "recipient";
    const occasion = formData.occasion || "special occasion";
    const budget = getBudgetText(formData.budget);
    const interests = formData.interests?.length
      ? formData.interests.join(", ")
      : "personalized gifts";
    const sports = formData.sports?.length ? formData.sports.join(", ") : "";
    const personality = formData.personality?.length
      ? formData.personality.join(", ")
      : "thoughtful";
    const lifestyle = formData.lifestyle || "everyday lifestyle";
    const emotionalNote = formData.emotionalNote || "";

    const baseSearch = `${relationship} ${occasion} ${interests} ${personality}`;

    return [
      {
        title: "Personalized Memory Gift Box",
        description:
          "A curated box with small meaningful items that can feel personal, emotional, and thoughtful.",
        price: budget,
        source: "Etsy",
        emoji: "🎁",
        match_reason: `Good for ${name} because it combines the occasion, relationship, and emotional context into one meaningful gift.`,
        emotional_score: 96,
        link: buildStoreUrl("Etsy", `personalized memory gift box ${baseSearch}`),
      },
      {
        title: "Interest-Based Premium Gift Set",
        description: `A gift set focused on interests like ${interests}.`,
        price: budget,
        source: "Amazon",
        emoji: "✨",
        match_reason: `This matches ${name}'s interests and gives a safe but useful gifting option.`,
        emotional_score: 91,
        link: buildStoreUrl("Amazon", `${interests} gift set for ${relationship}`),
      },
      {
        title: "Custom Engraved Keepsake",
        description:
          "A personalized keepsake with a name, date, message, or memory connected to the occasion.",
        price: budget,
        source: "Custom",
        emoji: "💝",
        match_reason: emotionalNote
          ? "The personal note suggests this gift should feel emotional and memorable."
          : "A keepsake works well when the goal is to make the gift feel personal.",
        emotional_score: 94,
        link: buildStoreUrl("Etsy", `custom engraved keepsake for ${relationship} ${occasion}`),
      },
      {
        title: "Lifestyle Upgrade Gift",
        description: `A practical gift that fits ${lifestyle} and can be used regularly.`,
        price: budget,
        source: "Amazon",
        emoji: "🌿",
        match_reason: `This is useful for someone with a ${personality.toLowerCase()} personality and daily lifestyle needs.`,
        emotional_score: 86,
        link: buildStoreUrl("Amazon", `useful lifestyle gift for ${relationship} ${occasion}`),
      },
      {
        title: "Experience-Inspired Gift",
        description:
          "A gift idea focused on creating a memory instead of only giving a physical product.",
        price: budget,
        source: "Uncommon Goods",
        emoji: "🌟",
        match_reason:
          "This is a good fit when you want the gift to feel unique, thoughtful, and less generic.",
        emotional_score: 89,
        link: buildStoreUrl("Uncommon", `unique experience gift ${relationship} ${occasion}`),
      },
      {
        title: sports ? "Sports-Themed Personalized Gift" : "Surprise Gift Bundle",
        description: sports
          ? `A personalized gift connected to sports interests like ${sports}.`
          : "A flexible surprise bundle based on the recipient's interests and personality.",
        price: budget,
        source: "Amazon",
        emoji: sports ? "🏆" : "🛍️",
        match_reason: sports
          ? `This connects directly with ${name}'s sports interests.`
          : `This works when you want a balanced, safe, and thoughtful gift for ${name}.`,
        emotional_score: 84,
        link: buildStoreUrl("Amazon", sports ? `${sports} personalized gift` : `${baseSearch} surprise gift`),
      },
    ];
  };

  const handleFormSubmit = async (formData) => {
    setIsLoadingAI(true);
    setRecipientName(formData.recipientName || "");
    setLastFormData(formData);

    try {
      const generatedGifts = createGiftIdeas(formData);
      setAiGifts(generatedGifts);
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleSaveGift = (gift) => {
    const savedGift = {
      id: Date.now(),
      ...createSavedGift(gift, lastFormData),
    };

    setSavedGifts((prev) => [savedGift, ...prev]);
  };

  const handleMarkBought = (id) => {
    setSavedGifts((prev) =>
      prev.map((gift) =>
        gift.id === id ? { ...gift, status: "bought" } : gift
      )
    );
  };

  const handleRemoveGift = (id) => {
    setSavedGifts((prev) => prev.filter((gift) => gift.id !== id));
  };

  const handleReset = () => {
    setAiGifts([]);
    setRecipientName("");
    setLastFormData({});
  };

  return (
    <div className="aiGiftViewport">
      <div className="aiGiftMainColumn">
        <div className="aiGiftFullHeader">
          <h1>AI Gifting</h1>
          <p>
            Collect recipient preferences and generate thoughtful gift ideas from
            Amazon, Etsy, and other e-commerce platforms.
          </p>
        </div>
  
        <HeroSection />
  
        {aiGifts.length === 0 && (
          <GiftInfoForm
            onSubmit={handleFormSubmit}
            isLoading={isLoadingAI}
          />
        )}
  
        <AIResultsSection
          gifts={aiGifts}
          recipientName={recipientName}
          onSave={handleSaveGift}
          onReset={handleReset}
        />
  
        <CuratedGiftIdeas />

      <RightSidebar
        savedGifts={savedGifts}
        onRemove={handleRemoveGift}
        onMarkBought={handleMarkBought}
      />
      </div>
    </div>
  );
}